<script lang="ts">
  export * from './Illustration.models';
</script>

<script lang="ts" setup>
  import type { StashColorNames, StashColorNamesWithShades } from '@leaflink/stash-types/colors';
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, inject } from 'vue';
  import InlineSvg from 'vue-inline-svg';

  import type { StashProvideState } from '../../../types/misc';
  import type { IllustrationTypes, SpotName, SpotNames, VignetteName, VignetteNames } from './Illustration.models';
  import { IllustrationType, spotNames, vignetteNames } from './Illustration.models';

  export interface IllustrationProps {
    id?: string;

    /**
     * The filename of the icon that should be displayed
     */
    name: SpotNames | VignetteNames;

    /**
     * Accessible, short-text description for the icon. Not rendered as part of the graphic, but
     * browsers usually display it as a tooltip and screen readers use this.
     */
    title?: string;

    /**
     * The size in pixels of the illustration.  This number will be used for width and height
     */
    size?: number;

    /**
     * Type of the illustration
     */
    type?: IllustrationTypes;

    /**
     * The fill color for the illustration.  This component will use the 400 shade of the color
     * per design's request.  Defaults to teal.
     */
    fillColor?: StashColorNames;

    /**
     * The color theme for the illustration.  This component will use the standard 500 shade of the color.
     * Defaults to purple
     */
    strokeColor?: StashColorNamesWithShades | 'white' | 'black';

    /**
     * Illustration's custom static path. It'll default to either the staticPath defined on the library installation or '/static' if none are provided.
     */
    staticPath?: string;
  }

  const props = withDefaults(defineProps<IllustrationProps>(), {
    id: uniqueId('ll-illustration-'),
    title: '',
    size: 48,
    type: IllustrationType.Spot,
    fillColor: 'blue',
    strokeColor: 'purple-500',
    staticPath: '',
  });

  if (props.type === IllustrationType.Scene) {
    throw new Error(
      'Scene illustration types are not supported. Illustrations will need to be uploaded under ./assets/illustrations/scene. Illustrations to be provided by design.',
    );
  }

  if (
    (props.type === IllustrationType.Spot && !spotNames.includes(props.name as SpotName)) ||
    (props.type === IllustrationType.Vignette && !vignetteNames.includes(props.name as VignetteName))
  ) {
    throw new Error(
      `${props.type} illustration name ${props.name} not found. Did you check that you are using the correct type for your illustration?`,
    );
  }

  const stashOptions = inject<Partial<StashProvideState>>('stashOptions', {
    staticPath: '/static',
  });

  const isSpotIllustration = computed(() => props.type === IllustrationType.Spot);

  /**
   * Browsers won't add the `stroke-width` attribute if it is set to false.
   * However, setting it to specific number value affects vignettes
   */
  const strokeWidth = computed<boolean | string>(() => {
    return isSpotIllustration.value ? '0' : false;
  });

  const computedStaticPath = computed(() => {
    return props.staticPath || stashOptions?.staticPath;
  });
</script>

<template>
  <!-- InlineSvg package (https://github.com/shrpne/vue-inline-svg) swaps external svgs and puts
  them inline so we can style them with CSS -->
  <!-- Having to override fill to be none and stroke to be zero so the exported svgs from Figma
  that do not have those attributes specified do not get a default one applied -->
  <InlineSvg
    :id="props.id"
    class="inline-block align-middle"
    :class="{
      [`text-${props.fillColor}-400 stroke-${props.strokeColor}`]: isSpotIllustration,
    }"
    :title="props.title"
    :height="props.size"
    :width="props.size"
    :stroke-width="strokeWidth"
    fill="none"
    :src="`${computedStaticPath}/illustrations/${props.type}/${props.name}.svg`"
  />
</template>
